'use client';

import TermSheetHeader from '@/app/tools/term-sheet/components/TermSheetHeader';
import styles from './termSheetLayout.module.scss';
import TermSheetPreview from '@/app/tools/term-sheet/components/TermSheetPreview';
import { cx } from 'class-variance-authority';
import { Button, Drawer } from '@baseline/ui';
import { OutputConfig } from '@/services/termSheet/types/common';
import { useRef } from 'react';

export type NavButtonProps = {
  disabled?: boolean;
  onClick?: () => void;
  text?: string;
  loading?: boolean;
};

export default function TermSheetLayout({
  children,
  hideFooter,
  nextButtonProps,
  backButtonProps,
  previewData,
  contentAsDrawer,
  showDemoButton = true
}: {
  children: React.ReactNode;
  hideFooter?: boolean;
  nextButtonProps?: NavButtonProps;
  backButtonProps?: NavButtonProps;
  previewData?: OutputConfig;
  contentAsDrawer?: boolean;
  showDemoButton?: boolean;
}) {
  const leftScrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollLeftToTop = () => {
    if (leftScrollContainerRef.current) {
      leftScrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const renderContent = () => {
    return (
      <>
        <div ref={leftScrollContainerRef} className={styles.scrollContainer}>
          <div className={styles.leftContent}>{children}</div>
        </div>
        {!hideFooter && (
          <div>
            <div className={styles.footerSpacer} />
            <div className={styles.footer}>
              <Button
                colorScheme="secondary"
                {...backButtonProps}
                onClick={() => {
                  scrollLeftToTop();
                  backButtonProps?.onClick?.();
                }}
              >
                {backButtonProps?.text || 'Back'}
              </Button>
              <Button
                className={styles.nextButton}
                {...nextButtonProps}
                onClick={() => {
                  scrollLeftToTop();
                  nextButtonProps?.onClick?.();
                }}
              >
                {nextButtonProps?.text || 'Next'}
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.termSheetLayout}>
      {contentAsDrawer ? (
        <div>
          <Drawer
            triggerClass={styles.drawerShareButton}
            renderTrigger={() => <Button size="large">Share</Button>}
            defaultOpen
          >
            <div className={styles.leftDrawer}>{renderContent()}</div>
          </Drawer>
        </div>
      ) : (
        <div className={styles.left}>
          <TermSheetHeader showDemoButton={showDemoButton} />
          {renderContent()}
        </div>
      )}
      <div
        className={cx(
          styles.scrollContainer,
          !contentAsDrawer && styles.previewContainerFull
        )}
      >
        <div
          className={cx(contentAsDrawer ? styles.rightDrawer : styles.right)}
          data-vaul-drawer-wrapper
        >
          {contentAsDrawer && (
            <TermSheetHeader position="fixed" showDemoButton={showDemoButton} />
          )}
          <TermSheetPreview data={previewData} />
        </div>
      </div>
    </div>
  );
}
